import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { rememberReturnPath } from '@/lib/session';
import { GardenProvider } from '@/context/GardenContext';
import { ProfileProvider } from '@/context/ProfileContext';
import { HemisphereProvider } from '@/context/HemisphereContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import '@/styles/globals.css';

// Lazy load pages for better performance
const Home           = lazy(() => import('@/pages/Home'));
const Dashboard      = lazy(() => import('@/pages/Dashboard'));
const PlantsPage     = lazy(() => import('@/pages/PlantsPage'));
const PlantDetailPage = lazy(() => import('@/pages/PlantDetailPage'));
const BiodynamicPage = lazy(() => import('@/pages/BiodynamicPage'));
const AdvicePage     = lazy(() => import('@/pages/AdvicePage'));
const AdviceArticlePage = lazy(() => import('@/pages/AdvicePage').then(m => ({ default: m.AdviceArticlePage })));
const MoonPlantingPage = lazy(() => import('@/pages/MoonPlantingPage'));
const MoonPlantingIndexPage = lazy(() => import('@/pages/MoonPlantingPage').then(m => ({ default: m.MoonPlantingIndexPage })));
const MyGardenPage   = lazy(() => import('@/pages/MyGardenPage'));
const DirectoryPage  = lazy(() => import('@/pages/DirectoryPage'));
const LoginPage      = lazy(() => import('@/pages/LoginPage'));
const PrivacyPage    = lazy(() => import('@/pages/PrivacyPage'));
const TermsPage      = lazy(() => import('@/pages/TermsPage'));
const AboutPage      = lazy(() => import('@/pages/AboutPage'));
const BlogsPage      = lazy(() => import('@/pages/BlogsPage'));
const BlogPage       = lazy(() => import('@/pages/BlogPage'));

function LoadingSpinner() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      flexDirection: 'column',
      gap: '1rem',
    }}>
      <span style={{ fontSize: '2.5rem', animation: 'float 2s ease-in-out infinite' }}>🌱</span>
      <p style={{ fontFamily: 'var(--font-hand)', color: 'var(--sage)', fontSize: '1.1rem' }}>Growing…</p>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Track the last protected page visited. When the session lapses, SWA's
  // server-side role check redirects to /login and drops the original URL, so
  // this is how the silent re-auth knows where to put them back.
  useEffect(() => {
    if (isAuthenticated) rememberReturnPath(location.pathname + location.search);
  }, [isAuthenticated, location.pathname, location.search]);

  if (loading) return <LoadingSpinner />;
  // AuthProvider already tried a silent bounce on load; if we're still here it
  // needs real interaction, so show the login page.
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AppLayout() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <main style={{ flex: 1 }}>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/"            element={<Home />} />
            <Route path="/login"       element={<LoginPage />} />
            <Route path="/plants"      element={<PlantsPage />} />
            <Route path="/plants/:id"  element={<PlantDetailPage />} />
            <Route path="/biodynamic"  element={<BiodynamicPage />} />
            <Route path="/advice"      element={<AdvicePage />} />
            <Route path="/advice/:slug" element={<AdviceArticlePage />} />
            <Route path="/moon-planting"                element={<MoonPlantingIndexPage hemisphere="northern" />} />
            <Route path="/moon-planting/southern"       element={<MoonPlantingIndexPage hemisphere="southern" />} />
            <Route path="/moon-planting/southern/:slug" element={<MoonPlantingPage hemisphere="southern" />} />
            <Route path="/moon-planting/:slug"          element={<MoonPlantingPage hemisphere="northern" />} />
            <Route path="/directory"   element={<DirectoryPage />} />
            <Route path="/privacy"     element={<PrivacyPage />} />
            <Route path="/terms"       element={<TermsPage />} />
            <Route path="/about"       element={<AboutPage />} />
            <Route path="/blogs"       element={<BlogsPage />} />
            <Route path="/blogs/:slug" element={<BlogPage />} />

            {/* Protected routes */}
            <Route path="/dashboard" element={
              <RequireAuth><Dashboard /></RequireAuth>
            } />
            <Route path="/my-garden/*" element={
              <RequireAuth><MyGardenPage /></RequireAuth>
            } />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <GardenProvider>
          <ProfileProvider>
            <HemisphereProvider>
              <AppLayout />
            </HemisphereProvider>
          </ProfileProvider>
        </GardenProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
